import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { 
  Bell, Send, Plus, Trash2, LogOut, Smartphone, History, 
  Settings, Signal, CheckCircle, XCircle, Clock, Calendar, Timer, Server, HelpCircle, Info, Copy, AlertTriangle, Filter, Pencil, ChevronDown, ChevronUp
} from "lucide-react";

// App Version
const APP_VERSION = "1.2.0";

// Expandable History Row Component
function HistoryRow({ item, getApnsStatusBadge, copyDebugDetails }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <>
      <TableRow 
        className="border-[#262626] cursor-pointer hover:bg-[#171717]" 
        data-testid={`history-row-${item.id}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <TableCell className="text-white font-medium">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-[#3B82F6] flex-shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-[#737373] flex-shrink-0" />
            )}
            <div className="flex flex-col min-w-0">
              <span className="truncate">{item.title}</span>
              {!isExpanded && (
                <span className="text-xs text-[#737373] truncate max-w-[150px]">{item.message}</span>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <div className="flex flex-col">
            <span className="text-xs text-white font-mono truncate max-w-[120px]">{item.apns_topic || "-"}</span>
            {item.apns_is_sandbox !== null && (
              <Badge className={`text-[10px] w-fit mt-1 ${item.apns_is_sandbox ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}`}>
                {item.apns_is_sandbox ? "Sandbox" : "Prod"}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell>{getApnsStatusBadge(item.apns_status_code)}</TableCell>
        <TableCell className="hidden md:table-cell">
          <span className="text-xs text-[#737373] truncate max-w-[100px] block">
            {item.apns_reason || "-"}
          </span>
        </TableCell>
        <TableCell className="text-[#737373] hidden sm:table-cell">
          <span className={item.devices_success < item.devices_sent ? "text-yellow-400" : "text-green-400"}>
            {item.devices_success}/{item.devices_sent}
          </span>
        </TableCell>
        <TableCell className="text-[#737373] text-xs">
          {new Date(item.sent_at).toLocaleString("de-DE", { 
            hour: "2-digit", 
            minute: "2-digit",
            day: "2-digit",
            month: "2-digit"
          })}
        </TableCell>
        <TableCell>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-[#737373] hover:text-[#3B82F6]"
            onClick={(e) => { e.stopPropagation(); copyDebugDetails(item); }}
            title="Debug-Details kopieren"
          >
            <Copy className="w-3 h-3" />
          </Button>
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow className="border-[#262626] bg-[#0A0A0A]">
          <TableCell colSpan={7} className="p-4">
            <div className="space-y-3">
              <div>
                <span className="text-xs text-[#737373] block mb-1">Vollständige Nachricht:</span>
                <p className="text-white text-sm whitespace-pre-wrap bg-[#171717] p-3 rounded-lg border border-[#262626]">
                  {item.message}
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div>
                  <span className="text-[#737373]">ID:</span>
                  <p className="text-white font-mono truncate">{item.id}</p>
                </div>
                <div>
                  <span className="text-[#737373]">APNs ID:</span>
                  <p className="text-white font-mono truncate">{item.apns_id || "-"}</p>
                </div>
                <div>
                  <span className="text-[#737373]">Status Code:</span>
                  <p className={`font-mono ${item.apns_status_code === 200 ? "text-green-400" : "text-red-400"}`}>
                    {item.apns_status_code || "-"}
                  </p>
                </div>
                <div>
                  <span className="text-[#737373]">Gesendet:</span>
                  <p className="text-white">{new Date(item.sent_at).toLocaleString("de-DE")}</p>
                </div>
              </div>
              {item.apns_reason && (
                <div className="p-2 bg-red-500/10 border border-red-500/30 rounded">
                  <span className="text-xs text-red-400">Fehler: {item.apns_reason}</span>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [history, setHistory] = useState([]);
  const [devices, setDevices] = useState([]);
  const [scheduledPushes, setScheduledPushes] = useState([]);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [historyEnvFilter, setHistoryEnvFilter] = useState("all");
  const [lastApnsResponse, setLastApnsResponse] = useState(null);
  const [showApnsDetails, setShowApnsDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("send");
  
  // Push form state
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [badge, setBadge] = useState(1);
  const [sound, setSound] = useState(true);
  const [sendToAll, setSendToAll] = useState(true);
  const [deviceToken, setDeviceToken] = useState("");
  const [environment, setEnvironment] = useState("production");
  
  // Scheduled push state
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  
  // Add app modal state
  const [showAddApp, setShowAddApp] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [newApp, setNewApp] = useState({
    name: "",
    bundle_id: "",
    apns_topic: "",  // iOS Bundle Identifier for APNs
    team_id: "",
    key_id: "",
    p8_key_content: "",
    is_production: false
  });

  // Admin state
  const [adminDevices, setAdminDevices] = useState([]);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);

  useEffect(() => {
    loadApps();
  }, []);

  useEffect(() => {
    if (selectedApp) {
      loadHistory(selectedApp);
      loadDevices(selectedApp);
      loadScheduledPushes(selectedApp);
    }
  }, [selectedApp]);

  const loadApps = async () => {
    try {
      const response = await axios.get(`${API}/apps`);
      setApps(response.data);
      if (response.data.length > 0 && !selectedApp) {
        setSelectedApp(response.data[0].id);
      }
    } catch (error) {
      toast.error("Fehler beim Laden der Apps");
    }
  };

  const loadHistory = async (appId, errorFilter = onlyErrors, envFilter = historyEnvFilter) => {
    try {
      let url = `${API}/push/history?app_id=${appId}&limit=50`;
      if (errorFilter) url += "&only_errors=true";
      if (envFilter === "sandbox") url += "&environment=sandbox";
      if (envFilter === "production") url += "&environment=production";
      
      const response = await axios.get(url);
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to load history:", error);
    }
  };

  // Reload history when filters change
  useEffect(() => {
    if (selectedApp) {
      loadHistory(selectedApp, onlyErrors, historyEnvFilter);
    }
  }, [onlyErrors, historyEnvFilter]);

  const loadDevices = async (appId) => {
    try {
      const response = await axios.get(`${API}/devices?app_id=${appId}`);
      setDevices(response.data);
    } catch (error) {
      console.error("Failed to load devices:", error);
    }
  };

  const loadScheduledPushes = async (appId) => {
    try {
      const response = await axios.get(`${API}/push/scheduled?app_id=${appId}`);
      setScheduledPushes(response.data);
    } catch (error) {
      console.error("Failed to load scheduled pushes:", error);
    }
  };

  const handleAddApp = async () => {
    if (!newApp.name || !newApp.bundle_id || !newApp.team_id || !newApp.key_id || !newApp.p8_key_content) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    
    setLoading(true);
    try {
      // Include apns_topic if provided
      const appData = {
        ...newApp,
        apns_topic: newApp.apns_topic || newApp.bundle_id  // Default to bundle_id if not set
      };
      await axios.post(`${API}/apps`, appData);
      toast.success("App hinzugefügt");
      setShowAddApp(false);
      setNewApp({ name: "", bundle_id: "", apns_topic: "", team_id: "", key_id: "", p8_key_content: "", is_production: false });
      loadApps();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateApp = async () => {
    if (!editingApp) return;
    
    setLoading(true);
    try {
      // Build update payload - only include changed/non-empty fields
      const updatePayload = {};
      if (editingApp.name) updatePayload.name = editingApp.name;
      if (editingApp.apns_topic) updatePayload.apns_topic = editingApp.apns_topic;
      if (editingApp.team_id) updatePayload.team_id = editingApp.team_id;
      if (editingApp.key_id) updatePayload.key_id = editingApp.key_id;
      if (editingApp.p8_key_content) updatePayload.p8_key_content = editingApp.p8_key_content;
      if (editingApp.is_production !== undefined) updatePayload.is_production = editingApp.is_production;
      
      await axios.patch(`${API}/apps/${editingApp.id}`, updatePayload);
      toast.success("App aktualisiert");
      setEditingApp(null);
      loadApps();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Aktualisieren");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApp = async (appId) => {
    if (!window.confirm("App wirklich löschen? Alle zugehörigen Daten werden gelöscht.")) return;
    
    try {
      await axios.delete(`${API}/apps/${appId}`);
      toast.success("App gelöscht");
      if (selectedApp === appId) {
        setSelectedApp(null);
      }
      loadApps();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleSendPush = async (e) => {
    e.preventDefault();
    if (!selectedApp) {
      toast.error("Bitte eine App auswählen");
      return;
    }
    if (!title || !message) {
      toast.error("Titel und Nachricht erforderlich");
      return;
    }
    if (!sendToAll && !deviceToken) {
      toast.error("Device Token erforderlich");
      return;
    }

    // If scheduled, validate date/time
    if (isScheduled) {
      if (!scheduledDate || !scheduledTime) {
        toast.error("Datum und Uhrzeit für geplante Nachricht erforderlich");
        return;
      }
      
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        toast.error("Geplante Zeit muss in der Zukunft liegen");
        return;
      }
    }

    setSendLoading(true);
    try {
      if (isScheduled) {
        // Schedule push
        const scheduledDateTime = `${scheduledDate}T${scheduledTime}`;
        const response = await axios.post(`${API}/push/schedule`, {
          app_id: selectedApp,
          title,
          message,
          badge,
          sound,
          send_to_all: sendToAll,
          device_token: sendToAll ? null : deviceToken,
          scheduled_time: scheduledDateTime,
          environment
        });
        
        toast.success(`Push geplant für ${response.data.scheduled_time_berlin} (${environment})`);
        loadScheduledPushes(selectedApp);
      } else {
        // Send immediately
        const response = await axios.post(`${API}/push/send`, {
          app_id: selectedApp,
          title,
          message,
          badge,
          sound,
          send_to_all: sendToAll,
          device_token: sendToAll ? null : deviceToken,
          environment
        });
        
        // Store APNs response for details modal
        setLastApnsResponse(response.data);
        
        // Show appropriate toast based on APNs status
        const apnsStatus = response.data.apns_status_code;
        const apnsReason = response.data.apns_reason;
        
        if (apnsStatus === 200 || response.data.devices_success > 0) {
          toast.success(
            <div>
              <div className="font-bold">APNs {apnsStatus || 200} OK</div>
              <div className="text-sm opacity-80">
                {response.data.devices_success}/{response.data.devices_sent} an APNs übergeben
              </div>
              <div className="text-xs opacity-60 mt-1">Topic: {response.data.apns_topic}</div>
            </div>
          );
        } else {
          toast.error(
            <div>
              <div className="font-bold">APNs {apnsStatus} Fehler</div>
              <div className="text-sm">{apnsReason}</div>
              <div className="text-xs opacity-60 mt-1">Topic: {response.data.apns_topic}</div>
            </div>
          );
          setShowApnsDetails(true);
        }
        
        loadHistory(selectedApp);
      }
      
      // Reset form
      setTitle("");
      setMessage("");
      setIsScheduled(false);
      setScheduledDate("");
      setScheduledTime("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSendLoading(false);
    }
  };

  // Copy APNs debug details to clipboard
  const copyDebugDetails = (data) => {
    const debugInfo = {
      apns_topic: data.apns_topic,
      apns_is_sandbox: data.apns_is_sandbox,
      apns_status_code: data.apns_status_code,
      apns_reason: data.apns_reason,
      apns_id: data.apns_id,
      devices_sent: data.devices_sent,
      devices_success: data.devices_success,
      timestamp: data.sent_at,
      apns_results: data.apns_results?.map(r => ({
        token: r.device_token_masked,
        status: r.apns_status_code,
        reason: r.apns_reason
      }))
    };
    navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
    toast.success("Debug-Details kopiert");
  };

  const handleCancelScheduled = async (scheduledId) => {
    if (!window.confirm("Geplante Nachricht wirklich abbrechen?")) return;
    
    try {
      await axios.delete(`${API}/push/scheduled/${scheduledId}`);
      toast.success("Geplante Nachricht abgebrochen");
      loadScheduledPushes(selectedApp);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Abbrechen");
    }
  };

  // ============== ADMIN FUNCTIONS ==============

  const loadAdminDevices = async () => {
    try {
      const response = await axios.get(`${API}/admin/devices`);
      setAdminDevices(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Geräte");
    }
  };

  const loadAdminNotifications = async () => {
    try {
      const response = await axios.get(`${API}/admin/notifications?limit=100`);
      setAdminNotifications(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Benachrichtigungen");
    }
  };

  const loadCleanupPreview = async () => {
    try {
      const response = await axios.get(`${API}/admin/cleanup/preview`);
      setCleanupPreview(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Cleanup-Vorschau");
    }
  };

  const handleDeleteDevice = async (deviceToken) => {
    if (!window.confirm("Gerät wirklich löschen? Alle zugehörigen Inbox-Daten werden gelöscht.")) return;
    
    try {
      const response = await axios.delete(`${API}/admin/devices/${encodeURIComponent(deviceToken)}`);
      toast.success(`Gerät gelöscht (${response.data.message_states_deleted} Inbox-Einträge entfernt)`);
      loadAdminDevices();
      if (selectedApp) loadDevices(selectedApp);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm("Nachricht wirklich löschen? Sie wird für ALLE Geräte entfernt.")) return;
    
    try {
      const response = await axios.delete(`${API}/admin/notifications/${notificationId}`);
      toast.success(`Nachricht gelöscht (${response.data.message_states_deleted} Inbox-Einträge entfernt)`);
      loadAdminNotifications();
      if (selectedApp) loadHistory(selectedApp);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleRunCleanup = async () => {
    if (!window.confirm(`Cleanup wirklich ausführen? ${cleanupPreview?.devices_to_delete || 0} Geräte werden gelöscht.`)) return;
    
    setAdminLoading(true);
    try {
      const response = await axios.post(`${API}/admin/cleanup/run`);
      toast.success(`Cleanup abgeschlossen: ${response.data.devices_deleted} Geräte, ${response.data.message_states_deleted} Inbox-Einträge gelöscht`);
      loadCleanupPreview();
      loadAdminDevices();
    } catch (error) {
      toast.error("Fehler beim Cleanup");
    } finally {
      setAdminLoading(false);
    }
  };

  const openAdminPanel = () => {
    setShowAdminPanel(true);
    loadAdminDevices();
    loadAdminNotifications();
    loadCleanupPreview();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  // APNs Status Badge with color coding
  const getApnsStatusBadge = (statusCode) => {
    if (statusCode === null || statusCode === undefined) {
      return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">-</Badge>;
    }
    if (statusCode === 200) {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">{statusCode}</Badge>;
    }
    if (statusCode >= 400 && statusCode < 500) {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">{statusCode}</Badge>;
    }
    if (statusCode >= 500) {
      return <Badge className="bg-red-600/20 text-red-500 border-red-600/30">{statusCode}</Badge>;
    }
    return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">{statusCode}</Badge>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "sent":
        return <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30"><CheckCircle className="w-3 h-3 mr-1" /> Gesendet</Badge>;
      case "partial":
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><AlertTriangle className="w-3 h-3 mr-1" /> Teilweise</Badge>;
      case "failed":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" /> Fehlgeschlagen</Badge>;
      case "sending":
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" /> Wird gesendet</Badge>;
      case "pending":
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30"><Timer className="w-3 h-3 mr-1" /> Geplant</Badge>;
      case "cancelled":
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30"><XCircle className="w-3 h-3 mr-1" /> Abgebrochen</Badge>;
      case "missed":
        return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30"><Clock className="w-3 h-3 mr-1" /> Verpasst</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Get minimum date/time for scheduler (now)
  const getMinDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  const getMinTime = () => {
    if (scheduledDate === getMinDate()) {
      const now = new Date();
      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    }
    return "00:00";
  };

  const currentApp = apps.find(a => a.id === selectedApp);
  const pendingScheduled = scheduledPushes.filter(s => s.status === "pending");

  return (
    <div className="min-h-screen bg-[#050505] pb-20 md:pb-0">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#3B82F6]/10 rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-[#3B82F6]" />
            </div>
            <span className="font-mono font-bold text-white hidden sm:block">Push Dashboard</span>
          </div>
          
          {/* App Selector */}
          <Select value={selectedApp || ""} onValueChange={setSelectedApp}>
            <SelectTrigger className="w-[200px] bg-[#171717] border-[#262626] text-white" data-testid="app-selector">
              <SelectValue placeholder="App auswählen" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#262626]">
              {apps.map(app => (
                <SelectItem key={app.id} value={app.id} className="text-white hover:bg-[#262626]">
                  <div className="flex items-center gap-2">
                    <Signal className="w-4 h-4 text-[#3B82F6]" />
                    {app.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={openAdminPanel} 
              className="text-[#737373] hover:text-[#3B82F6]" 
              data-testid="admin-panel-btn"
            >
              <Settings className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-[#737373] hover:text-white" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Push Form - Main Panel */}
          <div className="lg:col-span-8 space-y-6">
            <Card className="bg-[#0A0A0A] border-[#262626] card-hover" data-testid="push-form-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-mono text-white flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#3B82F6]" />
                  Push senden
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSendPush} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#737373]">Titel</Label>
                      <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="bg-[#171717] border-[#262626] text-white font-mono"
                        placeholder="Benachrichtigung"
                        data-testid="push-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="text-[#737373]">Badge</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-5 w-5 text-[#737373] hover:text-[#3B82F6]">
                              <HelpCircle className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 bg-[#0A0A0A] border-[#262626] text-white">
                            <div className="space-y-2">
                              <h4 className="font-mono font-bold text-[#3B82F6]">Was ist Badge?</h4>
                              <p className="text-sm text-[#737373]">
                                Die rote Zahl auf dem App-Icon, die ungelesene Nachrichten anzeigt.
                              </p>
                              <div className="space-y-1 text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-white">0</span>
                                  <span className="text-[#737373]">→ Keine Zahl anzeigen</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-white">1+</span>
                                  <span className="text-[#737373]">→ Zahl auf App-Icon</span>
                                </div>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <Input
                        type="number"
                        value={badge}
                        onChange={(e) => setBadge(parseInt(e.target.value) || 0)}
                        className="bg-[#171717] border-[#262626] text-white font-mono"
                        min={0}
                        data-testid="push-badge-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#737373]">Nachricht</Label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="bg-[#171717] border-[#262626] text-white font-mono min-h-[100px]"
                      placeholder="Deine Nachricht..."
                      data-testid="push-message-input"
                    />
                  </div>

                  {/* Environment Selection */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-[#737373]">Environment</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-5 w-5 text-[#737373] hover:text-[#3B82F6]">
                            <HelpCircle className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 bg-[#0A0A0A] border-[#262626] text-white">
                          <div className="space-y-3">
                            <h4 className="font-mono font-bold text-[#3B82F6]">Wann welches Environment?</h4>
                            <div className="space-y-3 text-sm">
                              <div className="p-2 bg-[#171717] rounded border border-[#262626]">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                                  <span className="font-mono text-white">Sandbox</span>
                                </div>
                                <p className="text-[#737373] text-xs">
                                  Für Entwicklung & Tests. Funktioniert nur mit Apps aus Xcode (Debug-Build). Keine echten User betroffen.
                                </p>
                              </div>
                              <div className="p-2 bg-[#171717] rounded border border-[#262626]">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="w-2 h-2 bg-[#3B82F6] rounded-full" />
                                  <span className="font-mono text-white">Production</span>
                                </div>
                                <p className="text-[#737373] text-xs">
                                  Für Live-Apps aus dem App Store oder TestFlight. Erreicht echte User!
                                </p>
                              </div>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <Select value={environment} onValueChange={setEnvironment}>
                      <SelectTrigger className="w-full md:w-[200px] bg-[#171717] border-[#262626] text-white" data-testid="environment-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                        <SelectItem value="production" className="text-white hover:bg-[#262626]">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-[#3B82F6] rounded-full" />
                            Production
                          </div>
                        </SelectItem>
                        <SelectItem value="sandbox" className="text-white hover:bg-[#262626]">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                            Sandbox
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#737373]">
                      {environment === "production" 
                        ? "api.push.apple.com (Live)" 
                        : "api.sandbox.push.apple.com (Entwicklung)"}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={sound}
                        onCheckedChange={setSound}
                        data-testid="push-sound-switch"
                      />
                      <Label className="text-[#737373]">Sound</Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={sendToAll}
                        onCheckedChange={setSendToAll}
                        data-testid="push-sendall-switch"
                      />
                      <Label className="text-[#737373]">An alle Geräte</Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={isScheduled}
                        onCheckedChange={setIsScheduled}
                        data-testid="push-scheduled-switch"
                      />
                      <Label className="text-[#737373] flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Planen
                      </Label>
                    </div>
                  </div>

                  {!sendToAll && (
                    <div className="space-y-2">
                      <Label className="text-[#737373]">Device Token</Label>
                      <Input
                        value={deviceToken}
                        onChange={(e) => setDeviceToken(e.target.value)}
                        className="bg-[#171717] border-[#262626] text-white font-mono text-sm"
                        placeholder="Device Token eingeben..."
                        data-testid="push-token-input"
                      />
                    </div>
                  )}

                  {isScheduled && (
                    <div className="bg-[#171717] p-4 rounded-lg border border-[#262626] space-y-4">
                      <div className="flex items-center gap-2 text-[#3B82F6]">
                        <Timer className="w-4 h-4" />
                        <span className="text-sm font-mono">Zeitplanung (Berliner Zeit)</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#737373]">Datum</Label>
                          <Input
                            type="date"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                            min={getMinDate()}
                            className="bg-[#0A0A0A] border-[#262626] text-white font-mono"
                            data-testid="scheduled-date-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#737373]">Uhrzeit</Label>
                          <Input
                            type="time"
                            value={scheduledTime}
                            onChange={(e) => setScheduledTime(e.target.value)}
                            min={getMinTime()}
                            className="bg-[#0A0A0A] border-[#262626] text-white font-mono"
                            data-testid="scheduled-time-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={sendLoading || !selectedApp}
                    className="w-full bg-[#3B82F6] text-black hover:bg-[#3B82F6]/90 font-mono font-bold btn-primary h-12"
                    data-testid="send-push-btn"
                  >
                    {sendLoading ? (
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : isScheduled ? (
                      <>
                        <Calendar className="w-5 h-5 mr-2" />
                        Push planen
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Push senden {currentApp && `(${currentApp.device_count} Geräte)`}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Tabs for History and Scheduled */}
            <Card className="bg-[#0A0A0A] border-[#262626] card-hover">
              <Tabs defaultValue="history" className="w-full">
                <CardHeader className="pb-2">
                  <TabsList className="bg-[#171717] border border-[#262626]">
                    <TabsTrigger 
                      value="history" 
                      className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-black"
                      data-testid="history-tab"
                    >
                      <History className="w-4 h-4 mr-2" />
                      Verlauf
                    </TabsTrigger>
                    <TabsTrigger 
                      value="scheduled" 
                      className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-black"
                      data-testid="scheduled-tab"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Geplant
                      {pendingScheduled.length > 0 && (
                        <Badge className="ml-2 bg-[#3B82F6] text-black text-xs px-1.5">
                          {pendingScheduled.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                  </TabsList>
                </CardHeader>

                <CardContent>
                  <TabsContent value="history" className="mt-0">
                    {/* History Filters */}
                    <div className="flex flex-wrap items-center gap-3 mb-4 p-2 bg-[#171717] rounded-lg">
                      <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-[#737373]" />
                        <span className="text-xs text-[#737373]">Filter:</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={onlyErrors}
                          onCheckedChange={setOnlyErrors}
                          className="scale-75"
                        />
                        <Label className="text-xs text-[#737373]">Nur Fehler</Label>
                      </div>
                      <Select value={historyEnvFilter} onValueChange={setHistoryEnvFilter}>
                        <SelectTrigger className="h-7 w-[110px] bg-[#0A0A0A] border-[#262626] text-xs text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                          <SelectItem value="all" className="text-white text-xs">Alle</SelectItem>
                          <SelectItem value="production" className="text-white text-xs">Production</SelectItem>
                          <SelectItem value="sandbox" className="text-white text-xs">Sandbox</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <ScrollArea className="h-[280px]">
                      <Table className="data-table">
                        <TableHeader>
                          <TableRow className="border-[#262626]">
                            <TableHead className="text-[#737373]">Titel</TableHead>
                            <TableHead className="text-[#737373] hidden lg:table-cell">Topic</TableHead>
                            <TableHead className="text-[#737373]">APNs</TableHead>
                            <TableHead className="text-[#737373] hidden md:table-cell">Reason</TableHead>
                            <TableHead className="text-[#737373] hidden sm:table-cell">Geräte</TableHead>
                            <TableHead className="text-[#737373]">Zeit</TableHead>
                            <TableHead className="text-[#737373] w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {history.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center text-[#737373] py-8">
                                {onlyErrors ? "Keine Fehler gefunden" : "Noch keine Nachrichten gesendet"}
                              </TableCell>
                            </TableRow>
                          ) : (
                            history.map((item) => (
                              <HistoryRow 
                                key={item.id} 
                                item={item} 
                                getApnsStatusBadge={getApnsStatusBadge}
                                copyDebugDetails={copyDebugDetails}
                              />
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </TabsContent>

                  <TabsContent value="scheduled" className="mt-0">
                    <ScrollArea className="h-[300px]">
                      <Table className="data-table">
                        <TableHeader>
                          <TableRow className="border-[#262626]">
                            <TableHead className="text-[#737373]">Titel</TableHead>
                            <TableHead className="text-[#737373] hidden sm:table-cell">Nachricht</TableHead>
                            <TableHead className="text-[#737373]">Geplant für</TableHead>
                            <TableHead className="text-[#737373]">Status</TableHead>
                            <TableHead className="text-[#737373]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {scheduledPushes.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-[#737373] py-8">
                                Keine geplanten Nachrichten
                              </TableCell>
                            </TableRow>
                          ) : (
                            scheduledPushes.map((item) => (
                              <TableRow key={item.id} className="border-[#262626]" data-testid={`scheduled-row-${item.id}`}>
                                <TableCell className="text-white font-medium">{item.title}</TableCell>
                                <TableCell className="text-[#737373] hidden sm:table-cell max-w-[200px] truncate">{item.message}</TableCell>
                                <TableCell className="text-[#3B82F6] font-mono">
                                  {item.scheduled_time_berlin}
                                </TableCell>
                                <TableCell>{getStatusBadge(item.status)}</TableCell>
                                <TableCell>
                                  {item.status === "pending" && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleCancelScheduled(item.id)}
                                      className="text-[#737373] hover:text-red-400 hover:bg-red-500/10"
                                      data-testid={`cancel-scheduled-${item.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {/* App Management */}
            <Card className="bg-[#0A0A0A] border-[#262626] card-hover" data-testid="apps-card">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-mono text-white flex items-center gap-2">
                    <Settings className="w-5 h-5 text-[#3B82F6]" />
                    Apps
                  </CardTitle>
                  <Dialog open={showAddApp} onOpenChange={setShowAddApp}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-[#3B82F6] text-black hover:bg-[#3B82F6]/90" data-testid="add-app-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-white font-mono">Neue App hinzufügen</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-[#737373]">App Name</Label>
                          <Input
                            value={newApp.name}
                            onChange={(e) => setNewApp({...newApp, name: e.target.value})}
                            className="bg-[#171717] border-[#262626] text-white"
                            placeholder="Meine App"
                            data-testid="new-app-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#737373]">Bundle ID</Label>
                          <Input
                            value={newApp.bundle_id}
                            onChange={(e) => setNewApp({...newApp, bundle_id: e.target.value})}
                            className="bg-[#171717] border-[#262626] text-white font-mono"
                            placeholder="com.example.app"
                            data-testid="new-app-bundle"
                          />
                          <p className="text-xs text-[#737373]">Interne App-ID (nicht änderbar)</p>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label className="text-[#737373]">APNs Topic</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-5 w-5 text-[#737373] hover:text-[#3B82F6]">
                                  <HelpCircle className="h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 bg-[#0A0A0A] border-[#262626] text-white">
                                <div className="space-y-2">
                                  <h4 className="font-mono font-bold text-[#3B82F6]">Was ist APNs Topic?</h4>
                                  <p className="text-sm text-[#737373]">
                                    Der iOS Bundle Identifier deiner App (z.B. <code className="text-white">app.PKVBeihilfetracker</code>).
                                  </p>
                                  <p className="text-sm text-[#737373]">
                                    Muss exakt mit dem Bundle Identifier in Xcode übereinstimmen!
                                  </p>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                          <Input
                            value={newApp.apns_topic}
                            onChange={(e) => setNewApp({...newApp, apns_topic: e.target.value})}
                            className="bg-[#171717] border-[#262626] text-white font-mono"
                            placeholder="app.example.com (iOS Bundle Identifier)"
                            data-testid="new-app-apns-topic"
                          />
                          <p className="text-xs text-[#737373]">Leer = Bundle ID wird verwendet</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-[#737373]">Team ID</Label>
                            <Input
                              value={newApp.team_id}
                              onChange={(e) => setNewApp({...newApp, team_id: e.target.value})}
                              className="bg-[#171717] border-[#262626] text-white font-mono"
                              placeholder="ABC123"
                              data-testid="new-app-team"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#737373]">Key ID</Label>
                            <Input
                              value={newApp.key_id}
                              onChange={(e) => setNewApp({...newApp, key_id: e.target.value})}
                              className="bg-[#171717] border-[#262626] text-white font-mono"
                              placeholder="XYZ789"
                              data-testid="new-app-key"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#737373]">P8 Key Content</Label>
                          <Textarea
                            value={newApp.p8_key_content}
                            onChange={(e) => setNewApp({...newApp, p8_key_content: e.target.value})}
                            className="bg-[#171717] border-[#262626] text-white font-mono text-xs min-h-[150px]"
                            placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                            data-testid="new-app-p8"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={newApp.is_production}
                            onCheckedChange={(checked) => setNewApp({...newApp, is_production: checked})}
                            data-testid="new-app-production"
                          />
                          <Label className="text-[#737373]">Production Environment</Label>
                        </div>
                        <Button
                          onClick={handleAddApp}
                          disabled={loading}
                          className="w-full bg-[#3B82F6] text-black hover:bg-[#3B82F6]/90 font-mono font-bold"
                          data-testid="save-app-btn"
                        >
                          {loading ? (
                            <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          ) : (
                            "App speichern"
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {apps.length === 0 ? (
                    <p className="text-[#737373] text-sm text-center py-4">
                      Noch keine Apps konfiguriert
                    </p>
                  ) : (
                    apps.map(app => (
                      <div 
                        key={app.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                          selectedApp === app.id 
                            ? "bg-[#3B82F6]/10 border-[#3B82F6]/30" 
                            : "bg-[#171717] border-[#262626] hover:border-[#3B82F6]/20"
                        }`}
                        onClick={() => setSelectedApp(app.id)}
                        data-testid={`app-item-${app.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium">{app.name}</p>
                          <p className="text-[#737373] text-xs font-mono truncate">{app.bundle_id}</p>
                          {app.apns_topic && app.apns_topic !== app.bundle_id && (
                            <p className="text-[#3B82F6] text-xs font-mono truncate">Topic: {app.apns_topic}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs border-[#262626] text-[#737373]">
                              <Smartphone className="w-3 h-3 mr-1" />
                              {app.device_count}
                            </Badge>
                            {app.is_production && (
                              <Badge className="text-xs bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30">
                                PROD
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setEditingApp({
                                ...app,
                                p8_key_content: ""  // Don't show existing key
                              }); 
                            }}
                            className="text-[#737373] hover:text-[#3B82F6] hover:bg-[#3B82F6]/10"
                            data-testid={`edit-app-${app.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleDeleteApp(app.id); }}
                            className="text-[#737373] hover:text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-app-${app.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Devices */}
            <Card className="bg-[#0A0A0A] border-[#262626] card-hover" data-testid="devices-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-mono text-white flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-[#3B82F6]" />
                  Geräte ({devices.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  {devices.length === 0 ? (
                    <p className="text-[#737373] text-sm text-center py-4">
                      Keine Geräte registriert
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {devices.slice(0, 10).map((device, i) => (
                        <div key={i} className="p-2 bg-[#171717] rounded border border-[#262626]">
                          <p className="text-white text-xs font-mono truncate">{device.device_token}</p>
                          <p className="text-[#737373] text-xs">
                            {device.platform} • {new Date(device.last_seen).toLocaleDateString("de-DE")}
                          </p>
                        </div>
                      ))}
                      {devices.length > 10 && (
                        <p className="text-[#737373] text-xs text-center">
                          +{devices.length - 10} weitere Geräte
                        </p>
                      )}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Legende / Hilfe */}
            <Card className="bg-[#0A0A0A] border-[#262626] card-hover" data-testid="help-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-mono text-white flex items-center gap-2">
                  <Info className="w-5 h-5 text-[#3B82F6]" />
                  Legende
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Badge Erklärung */}
                <div>
                  <h4 className="text-sm font-mono text-white mb-2">Badge-Nummer</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center gap-2 text-[#737373]">
                      <span className="font-mono bg-[#171717] px-2 py-0.5 rounded text-white">0</span>
                      <span>Keine Zahl auf App-Icon</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#737373]">
                      <span className="font-mono bg-[#171717] px-2 py-0.5 rounded text-white">1+</span>
                      <span>Rote Zahl auf App-Icon</span>
                    </div>
                  </div>
                </div>

                {/* Environment Erklärung */}
                <div>
                  <h4 className="text-sm font-mono text-white mb-2">Environment</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-start gap-2 text-[#737373]">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mt-1 shrink-0" />
                      <span><strong className="text-white">Sandbox:</strong> Xcode/Debug-Builds zum Testen</span>
                    </div>
                    <div className="flex items-start gap-2 text-[#737373]">
                      <div className="w-2 h-2 bg-[#3B82F6] rounded-full mt-1 shrink-0" />
                      <span><strong className="text-white">Production:</strong> App Store & TestFlight</span>
                    </div>
                  </div>
                </div>

                {/* Status Erklärung */}
                <div>
                  <h4 className="text-sm font-mono text-white mb-2">Status</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-400" />
                      <span className="text-[#737373]">Gesendet - Erfolgreich zugestellt</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Timer className="w-3 h-3 text-blue-400" />
                      <span className="text-[#737373]">Geplant - Wartet auf Sendezeit</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3 text-yellow-400" />
                      <span className="text-[#737373]">Wird gesendet - In Bearbeitung</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="w-3 h-3 text-red-400" />
                      <span className="text-[#737373]">Fehlgeschlagen - Prüfe Token/Key</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 glass border-t border-white/10 px-4 py-3 z-50">
        <div className="flex items-center justify-around">
          <Button variant="ghost" className="flex flex-col items-center gap-1 text-[#3B82F6]">
            <Send className="w-5 h-5" />
            <span className="text-xs">Senden</span>
          </Button>
          <Button variant="ghost" className="flex flex-col items-center gap-1 text-[#737373]">
            <History className="w-5 h-5" />
            <span className="text-xs">Verlauf</span>
          </Button>
          <Button variant="ghost" className="flex flex-col items-center gap-1 text-[#737373]" onClick={() => setShowAddApp(true)}>
            <Settings className="w-5 h-5" />
            <span className="text-xs">Apps</span>
          </Button>
        </div>
      </nav>

      {/* APNs Details Modal */}
      <Dialog open={showApnsDetails} onOpenChange={setShowApnsDetails}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-mono flex items-center gap-2">
              {lastApnsResponse?.apns_status_code === 200 ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-400" />
              )}
              APNs Response Details
            </DialogTitle>
          </DialogHeader>
          {lastApnsResponse && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-2 bg-[#171717] rounded">
                  <span className="text-[#737373] text-xs">Status Code</span>
                  <p className={`font-mono ${lastApnsResponse.apns_status_code === 200 ? "text-green-400" : "text-red-400"}`}>
                    {lastApnsResponse.apns_status_code || "N/A"}
                  </p>
                </div>
                <div className="p-2 bg-[#171717] rounded">
                  <span className="text-[#737373] text-xs">Environment</span>
                  <p className="text-white font-mono">
                    {lastApnsResponse.apns_is_sandbox ? "Sandbox" : "Production"}
                  </p>
                </div>
                <div className="p-2 bg-[#171717] rounded col-span-2">
                  <span className="text-[#737373] text-xs">Topic (Bundle ID)</span>
                  <p className="text-white font-mono text-xs break-all">{lastApnsResponse.apns_topic || "N/A"}</p>
                </div>
                {lastApnsResponse.apns_reason && (
                  <div className="p-2 bg-[#171717] rounded col-span-2 border border-red-500/30">
                    <span className="text-red-400 text-xs">Error Reason</span>
                    <p className="text-red-400 font-mono">{lastApnsResponse.apns_reason}</p>
                  </div>
                )}
                {lastApnsResponse.apns_id && (
                  <div className="p-2 bg-[#171717] rounded col-span-2">
                    <span className="text-[#737373] text-xs">APNs ID</span>
                    <p className="text-white font-mono text-xs break-all">{lastApnsResponse.apns_id}</p>
                  </div>
                )}
                <div className="p-2 bg-[#171717] rounded">
                  <span className="text-[#737373] text-xs">Gesendet</span>
                  <p className="text-white font-mono">{lastApnsResponse.devices_sent}</p>
                </div>
                <div className="p-2 bg-[#171717] rounded">
                  <span className="text-[#737373] text-xs">Erfolgreich</span>
                  <p className={`font-mono ${lastApnsResponse.devices_success === lastApnsResponse.devices_sent ? "text-green-400" : "text-yellow-400"}`}>
                    {lastApnsResponse.devices_success}
                  </p>
                </div>
              </div>
              
              {/* Per-device results */}
              {lastApnsResponse.apns_results && lastApnsResponse.apns_results.length > 0 && (
                <div className="p-2 bg-[#171717] rounded">
                  <span className="text-[#737373] text-xs mb-2 block">Ergebnisse pro Gerät</span>
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {lastApnsResponse.apns_results.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-1 bg-[#0A0A0A] rounded">
                        <span className="text-[#737373] font-mono">{r.device_token_masked}</span>
                        <div className="flex items-center gap-2">
                          {r.success ? (
                            <CheckCircle className="w-3 h-3 text-green-400" />
                          ) : (
                            <XCircle className="w-3 h-3 text-red-400" />
                          )}
                          <span className={r.success ? "text-green-400" : "text-red-400"}>
                            {r.apns_status_code} {r.apns_reason && `- ${r.apns_reason}`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <Button
                onClick={() => copyDebugDetails(lastApnsResponse)}
                variant="outline"
                className="w-full border-[#262626] text-white hover:bg-[#262626]"
              >
                <Copy className="w-4 h-4 mr-2" />
                Debug-Details kopieren
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit App Modal */}
      <Dialog open={!!editingApp} onOpenChange={(open) => !open && setEditingApp(null)}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-mono flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#3B82F6]" />
              App bearbeiten
            </DialogTitle>
          </DialogHeader>
          {editingApp && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#737373]">App Name</Label>
                <Input
                  value={editingApp.name || ""}
                  onChange={(e) => setEditingApp({...editingApp, name: e.target.value})}
                  className="bg-[#171717] border-[#262626] text-white"
                  placeholder="Meine App"
                  data-testid="edit-app-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#737373]">Bundle ID (nicht änderbar)</Label>
                <Input
                  value={editingApp.bundle_id || ""}
                  disabled
                  className="bg-[#171717] border-[#262626] text-[#737373] font-mono"
                  data-testid="edit-app-bundle"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-[#737373]">APNs Topic</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-5 w-5 text-[#737373] hover:text-[#3B82F6]">
                        <HelpCircle className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 bg-[#0A0A0A] border-[#262626] text-white">
                      <div className="space-y-2">
                        <h4 className="font-mono font-bold text-[#3B82F6]">Was ist APNs Topic?</h4>
                        <p className="text-sm text-[#737373]">
                          Der iOS Bundle Identifier deiner App (z.B. <code className="text-white">app.PKVBeihilfetracker</code>).
                        </p>
                        <p className="text-sm text-[#737373]">
                          Muss exakt mit dem Bundle Identifier in Xcode übereinstimmen!
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Input
                  value={editingApp.apns_topic || ""}
                  onChange={(e) => setEditingApp({...editingApp, apns_topic: e.target.value})}
                  className="bg-[#171717] border-[#262626] text-white font-mono"
                  placeholder="app.example.com (iOS Bundle Identifier)"
                  data-testid="edit-app-apns-topic"
                />
                <p className="text-xs text-[#737373]">Leer = Bundle ID wird verwendet</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#737373]">Team ID</Label>
                  <Input
                    value={editingApp.team_id || ""}
                    onChange={(e) => setEditingApp({...editingApp, team_id: e.target.value})}
                    className="bg-[#171717] border-[#262626] text-white font-mono"
                    placeholder="ABC123"
                    data-testid="edit-app-team"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#737373]">Key ID</Label>
                  <Input
                    value={editingApp.key_id || ""}
                    onChange={(e) => setEditingApp({...editingApp, key_id: e.target.value})}
                    className="bg-[#171717] border-[#262626] text-white font-mono"
                    placeholder="XYZ789"
                    data-testid="edit-app-key"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#737373]">P8 Key Content (leer lassen = unverändert)</Label>
                <Textarea
                  value={editingApp.p8_key_content || ""}
                  onChange={(e) => setEditingApp({...editingApp, p8_key_content: e.target.value})}
                  className="bg-[#171717] border-[#262626] text-white font-mono text-xs min-h-[120px]"
                  placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                  data-testid="edit-app-p8"
                />
                <p className="text-xs text-[#737373]">Nur ausfüllen wenn Key geändert werden soll</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingApp.is_production || false}
                  onCheckedChange={(checked) => setEditingApp({...editingApp, is_production: checked})}
                  data-testid="edit-app-production"
                />
                <Label className="text-[#737373]">Production Environment</Label>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setEditingApp(null)}
                  variant="outline"
                  className="flex-1 border-[#262626] text-white hover:bg-[#262626]"
                  data-testid="cancel-edit-btn"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleUpdateApp}
                  disabled={loading}
                  className="flex-1 bg-[#3B82F6] text-black hover:bg-[#3B82F6]/90 font-mono font-bold"
                  data-testid="save-edit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Speichern"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Panel Modal */}
      <Dialog open={showAdminPanel} onOpenChange={setShowAdminPanel}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-mono flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#3B82F6]" />
              Admin-Verwaltung
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="devices" className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-[#171717]">
              <TabsTrigger value="devices" className="data-[state=active]:bg-[#3B82F6]/20 data-[state=active]:text-[#3B82F6]">
                <Smartphone className="w-4 h-4 mr-2" />
                Geräte ({adminDevices.length})
              </TabsTrigger>
              <TabsTrigger value="notifications" className="data-[state=active]:bg-[#3B82F6]/20 data-[state=active]:text-[#3B82F6]">
                <Bell className="w-4 h-4 mr-2" />
                Nachrichten ({adminNotifications.length})
              </TabsTrigger>
              <TabsTrigger value="cleanup" className="data-[state=active]:bg-[#3B82F6]/20 data-[state=active]:text-[#3B82F6]">
                <Trash2 className="w-4 h-4 mr-2" />
                Cleanup
              </TabsTrigger>
            </TabsList>

            {/* Devices Tab */}
            <TabsContent value="devices" className="mt-4">
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {adminDevices.length === 0 ? (
                  <p className="text-[#737373] text-center py-8">Keine Geräte registriert</p>
                ) : (
                  adminDevices.map((device) => (
                    <div key={device.device_token} className="flex items-center justify-between p-3 bg-[#171717] rounded-lg border border-[#262626]">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-mono truncate">{device.device_token.slice(0, 20)}...{device.device_token.slice(-8)}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs border-[#262626] text-[#737373]">{device.app_name}</Badge>
                          <Badge variant="outline" className={`text-xs ${device.is_sandbox ? "border-yellow-500/30 text-yellow-400" : "border-green-500/30 text-green-400"}`}>
                            {device.is_sandbox ? "Sandbox" : "Production"}
                          </Badge>
                          <span className="text-xs text-[#737373]">Zuletzt: {new Date(device.last_seen).toLocaleString("de-DE")}</span>
                        </div>
                        {device.effective_apns_topic && (
                          <p className="text-xs text-[#3B82F6] mt-1 font-mono">Topic: {device.effective_apns_topic}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteDevice(device.device_token)}
                        className="text-[#737373] hover:text-red-400 hover:bg-red-500/10 ml-2"
                        data-testid={`delete-device-${device.device_token.slice(0, 8)}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="mt-4">
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {adminNotifications.length === 0 ? (
                  <p className="text-[#737373] text-center py-8">Keine Nachrichten</p>
                ) : (
                  adminNotifications.map((notif) => (
                    <div key={notif.id} className="flex items-center justify-between p-3 bg-[#171717] rounded-lg border border-[#262626]">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium">{notif.title}</p>
                        <p className="text-[#737373] text-sm truncate">{notif.message}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs border-[#262626] text-[#737373]">{notif.app_name}</Badge>
                          {getApnsStatusBadge(notif.apns_status_code)}
                          <span className="text-xs text-[#737373]">{new Date(notif.sent_at).toLocaleString("de-DE")}</span>
                          <span className="text-xs text-[#737373]">{notif.devices_success}/{notif.devices_sent} OK</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteNotification(notif.id)}
                        className="text-[#737373] hover:text-red-400 hover:bg-red-500/10 ml-2"
                        data-testid={`delete-notif-${notif.id.slice(0, 8)}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Cleanup Tab */}
            <TabsContent value="cleanup" className="mt-4">
              <div className="space-y-4">
                {cleanupPreview ? (
                  <>
                    <div className="p-4 bg-[#171717] rounded-lg border border-[#262626]">
                      <h4 className="text-white font-mono mb-2">Cleanup-Vorschau</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-[#737373]">Schwellwert:</span>
                          <p className="text-white">{cleanupPreview.inactive_days_threshold} Tage Inaktivität</p>
                        </div>
                        <div>
                          <span className="text-[#737373]">Stichtag:</span>
                          <p className="text-white">{new Date(cleanupPreview.cutoff_date).toLocaleString("de-DE")}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-[#737373]">Geräte zum Löschen:</span>
                          <p className={`text-xl font-mono ${cleanupPreview.devices_to_delete > 0 ? "text-yellow-400" : "text-green-400"}`}>
                            {cleanupPreview.devices_to_delete}
                          </p>
                        </div>
                      </div>
                      
                      {cleanupPreview.sample_devices?.length > 0 && (
                        <div className="mt-4">
                          <span className="text-[#737373] text-sm">Beispiel-Geräte:</span>
                          <div className="mt-2 space-y-1 max-h-[100px] overflow-y-auto">
                            {cleanupPreview.sample_devices.map((d, i) => (
                              <div key={i} className="text-xs font-mono text-[#737373] flex justify-between">
                                <span>{d.bundle_id}</span>
                                <span>{new Date(d.last_seen).toLocaleDateString("de-DE")}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={loadCleanupPreview}
                        variant="outline"
                        className="flex-1 border-[#262626] text-white hover:bg-[#262626]"
                      >
                        Aktualisieren
                      </Button>
                      <Button
                        onClick={handleRunCleanup}
                        disabled={adminLoading || cleanupPreview.devices_to_delete === 0}
                        className="flex-1 bg-red-600 text-white hover:bg-red-700 font-mono"
                      >
                        {adminLoading ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Cleanup ausführen
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <p className="text-xs text-[#737373] text-center">
                      Automatischer Cleanup läuft täglich um 03:00 Uhr (Berlin)
                    </p>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-[#737373]">Lade Vorschau...</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-[#262626] mt-8 py-4 text-center text-[#737373] text-xs hidden lg:block">
        <p>© {new Date().getFullYear()} Creativemedia · Version {APP_VERSION}</p>
      </footer>
    </div>
  );
}
